import { useState, useEffect, useRef } from "react";

interface ProgressBarProps {
  progress: number;
  className?: string;
}

const SEGMENT_PERCENT = 5;
const TOTAL_SEGMENTS = 100 / SEGMENT_PERCENT;
// Average delay between successive segments turning on. The total animation
// time is `N * STEP_INTERVAL_MS` for an N-segment jump; the easing function
// below redistributes that budget so cells flick on rapidly at the start
// and visibly slow toward the end (quadratic ease-out).
const STEP_INTERVAL_MS = 60;

// Quadratic ease-out: progress f(t) = 1 − (1−t)². Returns the delay before
// the `stepIndex`-th cell lights up (1-indexed) given an N-step animation
// of total duration `totalMs`. For step 1 the delay is measured from t=0;
// for step i>1 it's the gap from step i−1.
function easeOutQuadDelay(stepIndex: number, total: number, totalMs: number): number {
  const prev = Math.sqrt(1 - (stepIndex - 1) / total);
  const curr = Math.sqrt(1 - stepIndex / total);
  return totalMs * (prev - curr);
}

export function ProgressBar({ progress, className = "" }: ProgressBarProps) {
  const clamped = Math.min(100, Math.max(0, progress));
  const target = Math.min(TOTAL_SEGMENTS, Math.ceil(clamped / SEGMENT_PERCENT));

  const [filled, setFilled] = useState(target);
  // Mirror of `filled` for use inside the timer callback without re-creating
  // the effect on every step. State is for rendering; ref is for control.
  const filledRef = useRef(target);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    // Forward-only — ignore regressions. The worker can briefly emit a
    // smaller progress value if a daemon-thread tick lands after a larger
    // sync update.
    if (target <= filledRef.current) return;

    const startFilled = filledRef.current;
    const N = target - startFilled;
    const totalMs = N * STEP_INTERVAL_MS;

    let stepIndex = 0;
    const tick = () => {
      stepIndex += 1;
      filledRef.current = startFilled + stepIndex;
      setFilled(filledRef.current);
      if (filledRef.current < target) {
        const delay = easeOutQuadDelay(stepIndex + 1, N, totalMs);
        timerRef.current = setTimeout(tick, delay);
      } else {
        timerRef.current = null;
      }
    };
    timerRef.current = setTimeout(tick, easeOutQuadDelay(1, N, totalMs));

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [target]);

  return (
    <div className={`flex-1 flex items-center gap-[2px] ${className}`}>
      {Array.from({ length: TOTAL_SEGMENTS }, (_, i) => (
        <div
          key={i}
          className={`flex-1 aspect-[1/2] transition-colors duration-300 ${
            i < filled ? "bg-blue-500" : "bg-slate-800"
          }`}
        />
      ))}
    </div>
  );
}
